/**
 * Who the CRM search button is for, and what each role can see.
 * Scope lists match functions/src/agentVisibilityDefaults.ts.
 * Employees and admins can search. 1099 contractors never search (even role=staff).
 * Other roles stay off until that widens.
 */

export type SearchVisibilityRow = {
  id: string;
  role: string;
  how: string;
  canSearch: boolean;
  sees: string[];
  hidden: string[];
};

const BASE = ['search.general', 'chat.reply'] as const;

const DATA: Array<{ id: string; label: string; when: (scopes: Set<string>) => boolean }> = [
  { id: 'leads', label: 'Leads', when: (s) => s.has('leads.read') },
  { id: 'deals', label: 'Deals', when: (s) => s.has('deals.read') },
  { id: 'projects', label: 'Projects', when: (s) => s.has('projects.read') },
  { id: 'prices', label: 'Prices', when: (s) => s.has('price.read') },
  {
    id: 'ownPnl',
    label: 'P&L on their own jobs',
    when: (s) => s.has('pnl.read_own') && !s.has('pnl.read_all'),
  },
  { id: 'allPnl', label: 'All project P&L', when: (s) => s.has('pnl.read_all') },
  {
    id: 'cost',
    label: 'Cost and margin',
    when: (s) => s.has('cost.read') || s.has('pnl.read_all'),
  },
  { id: 'restricted', label: 'Restricted records', when: (s) => s.has('search.restricted') },
  { id: 'wages', label: 'Wages', when: (s) => s.has('wages.read') },
];

type Preset = {
  id: string;
  role: string;
  how: string;
  accountRole: string;
  designations?: string[];
  orgDepartment?: 'sales' | 'office' | 'shop' | null;
};

const PRESETS: Preset[] = [
  { id: 'admin', role: 'Admin', how: 'Admin account', accountRole: 'admin' },
  { id: 'employee', role: 'Employee', how: 'Employee account', accountRole: 'employee' },
  {
    id: 'sales',
    role: 'Sales',
    how: 'Sales designation',
    accountRole: 'staff',
    designations: ['sales_rep'],
    orgDepartment: 'sales',
  },
  {
    id: 'pm',
    role: 'Project manager',
    how: 'Project manager designation',
    accountRole: 'employee',
    designations: ['project_manager'],
  },
  {
    id: 'office',
    role: 'Office',
    how: 'Office manager',
    accountRole: 'employee_office',
    designations: ['office_manager'],
    orgDepartment: 'office',
  },
  {
    id: 'shop',
    role: 'Shop',
    how: 'Shop account',
    accountRole: 'employee_shop',
    orgDepartment: 'shop',
  },
  { id: 'cpa', role: 'CPA', how: 'CPA account', accountRole: 'cpa' },
  {
    id: 'estimator',
    role: 'Estimator',
    how: 'Estimator designation',
    accountRole: 'employee',
    designations: ['estimator'],
  },
  {
    id: 'cfo',
    role: 'CFO',
    how: 'CFO designation',
    accountRole: 'employee',
    designations: ['cfo'],
  },
  {
    id: 'coo',
    role: 'COO',
    how: 'COO designation',
    accountRole: 'employee',
    designations: ['coo'],
  },
];

const EMPLOYEE_SEARCH_ROLES = new Set([
  'admin',
  'employee',
  'staff',
  'employee_shop',
  'employee_office',
]);

/**
 * Cmd-K / search button gate. 1099 contractors are always denied even when
 * their account role is `staff` / `employee_*`.
 */
export function canUseCrmSearch(
  role: string | undefined | null,
  opts?: { is1099Contractor?: boolean | null },
): boolean {
  if (opts?.is1099Contractor === true) return false;
  return EMPLOYEE_SEARCH_ROLES.has(String(role || '').trim().toLowerCase());
}

function scopesFor(preset: Preset): Set<string> {
  const scopes = new Set<string>(BASE);
  const add = (list: readonly string[]) => {
    for (const scope of list) scopes.add(scope);
  };
  if (preset.accountRole === 'admin') {
    for (const item of DATA) {
      if (item.id === 'ownPnl') scopes.add('pnl.read_own');
      if (item.id === 'leads') scopes.add('leads.read');
      if (item.id === 'deals') scopes.add('deals.read');
      if (item.id === 'projects') scopes.add('projects.read');
      if (item.id === 'prices') scopes.add('price.read');
      if (item.id === 'allPnl') scopes.add('pnl.read_all');
      if (item.id === 'cost') scopes.add('cost.read');
      if (item.id === 'restricted') scopes.add('search.restricted');
      if (item.id === 'wages') scopes.add('wages.read');
    }
    return scopes;
  }
  if (preset.accountRole === 'employee') {
    add(['leads.read', 'deals.read', 'projects.read', 'price.read']);
  }
  if (
    preset.accountRole === 'staff'
    || preset.accountRole === 'employee_office'
    || preset.accountRole === 'employee_shop'
  ) {
    add(['projects.read', 'price.read']);
  }
  if (preset.accountRole === 'cpa') {
    add(['cost.read', 'pnl.read_all', 'search.restricted', 'projects.read', 'deals.read']);
  }
  if (preset.orgDepartment === 'sales') add(['dept.sales', 'leads.read', 'deals.read', 'price.read']);
  if (preset.orgDepartment === 'office') add(['dept.office', 'projects.read', 'price.read']);
  if (preset.orgDepartment === 'shop') add(['dept.shop', 'projects.read']);
  for (const designation of preset.designations || []) {
    if (designation === 'sales_rep') {
      add(['dept.sales', 'leads.read', 'leads.write_own', 'deals.read', 'deals.write_own', 'price.read']);
    }
    if (designation === 'project_manager') {
      add(['projects.read', 'projects.write_own', 'pnl.read_own', 'price.read']);
    }
    if (designation === 'estimator') {
      add(['price.read', 'cost.read', 'search.restricted', 'projects.read', 'deals.read']);
    }
    if (designation === 'cfo' || designation === 'coo') {
      add(['cost.read', 'pnl.read_all', 'wages.read', 'search.restricted', 'leads.read', 'deals.read', 'projects.read']);
    }
    if (designation === 'office_manager' || designation === 'executive_assistant') {
      add(['dept.office', 'leads.read', 'deals.read', 'projects.read', 'price.read', 'cost.read', 'search.restricted']);
    }
  }
  return scopes;
}

function describe(scopes: Set<string>): { sees: string[]; hidden: string[] } {
  const sees = DATA.filter((item) => item.when(scopes)).map((item) => item.label);
  const hidden = DATA.filter((item) => {
    if (item.id === 'ownPnl' && scopes.has('pnl.read_all')) return false;
    return !item.when(scopes);
  }).map((item) => item.label);
  return { sees, hidden };
}

export function searchVisibilityRows(): SearchVisibilityRow[] {
  return PRESETS.map((preset) => {
    const described = describe(scopesFor(preset));
    return {
      id: preset.id,
      role: preset.role,
      how: preset.how,
      canSearch: EMPLOYEE_SEARCH_ROLES.has(preset.accountRole),
      sees: described.sees,
      hidden: described.hidden,
    };
  });
}
